import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display order. Names must match the category values stored on products —
// "Riding Gloves" and "Communication Devices" are the DB spellings.
const CATEGORY_ORDER = [
  'Helmets',
  'Jackets',
  'Pants',
  'Boots',
  'Communication Devices',
  'Riding Gloves',
  'Performance Parts',
  'Hard Parts',
  'Helmet Spares',
  'Auxiliary Lights',
  'Soft Luggage',
];

const TILE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='800' viewBox='0 0 600 800'%3E%3Crect width='600' height='800' fill='%231a1a2e'/%3E%3C/svg%3E";

// These three curated shots fill the tile edge-to-edge instead of the default
// letterboxed fit — the rest stay letterboxed since they're cut-outs that
// would crop badly if stretched or cover-cropped.
const STRETCH_FIT = new Set(['Helmets', 'Jackets', 'Pants']);

// Curated shots, one per category — replaces the auto-picked first product
// photo from the API, which was inconsistent in framing and, for a few
// categories, effectively invisible (near-black cut-outs on a dark tile).
const CATEGORY_IMAGES = {
  'Helmets': '/images/categories/helmets.jpg',
  'Jackets': '/images/categories/jackets.jpg',
  'Pants': '/images/categories/pants.jpg',
  'Boots': '/images/categories/boots.jpg',
  'Communication Devices': '/images/categories/communication-devices.jpg',
  'Riding Gloves': '/images/categories/riding-gloves.jpg',
  'Performance Parts': '/images/categories/performance-parts.jpg',
  'Hard Parts': '/images/categories/hard-parts.jpg',
  'Helmet Spares': '/images/categories/helmet-spares.jpg',
  'Auxiliary Lights': '/images/categories/auxiliary-lights.jpg',
  'Soft Luggage': '/images/categories/soft-luggage.jpg',
};

const CategoryShowcase = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        if (cancelled) return;
        const byName = new Map(response.data.map(c => [c.name, c]));
        // Known categories in shop order, then anything unexpected the admin
        // has added since, so a new category never silently disappears.
        const ordered = [
          ...CATEGORY_ORDER.filter(n => byName.has(n)).map(n => byName.get(n)),
          ...response.data.filter(c => !CATEGORY_ORDER.includes(c.name)),
        ];
        setCategories(ordered);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  if (categories.length === 0) return null;

  return (
    <section id="categories" className="category-strip">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Shop by Category</h2>
        </div>

        <div className="category-stack">
          {categories.map((cat) => (
            <button
              key={cat.name}
              type="button"
              className="category-tile"
              onClick={() => navigate(`/catalog/${encodeURIComponent(cat.name)}`)}
              aria-label={`Browse ${cat.name}`}
            >
              <img
                src={CATEGORY_IMAGES[cat.name] || cat.image || TILE_PLACEHOLDER}
                alt=""
                className={`category-tile-img ${STRETCH_FIT.has(cat.name) ? 'category-tile-img-stretch' : ''}`}
                loading="lazy"
                decoding="async"
                draggable={false}
                onError={(e) => { e.currentTarget.src = TILE_PLACEHOLDER; }}
              />
              <span className="category-tile-scrim" />
              <span className="category-tile-body">
                <span className="category-tile-name">{cat.name}</span>
                <span className="category-tile-count">{cat.count} products</span>
                <span className="category-tile-cta">DISCOVER NOW</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryShowcase;
